import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { portfolioPreviewImages as defaultImages } from '@/mocks/home';
import EditableImage from '@/components/base/EditableImage';

const STORAGE_KEY = 'jb_home_preview_config';

interface PreviewImage {
  id: number;
  src: string;
  alt: string;
  category: string;
}

function loadPreviewImages(): PreviewImage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PreviewImage[];
      if (Array.isArray(parsed) && parsed.length === 6) return parsed;
    }
  } catch { /* ignore */ }
  return defaultImages;
}

export default function PortfolioPreview() {
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);
  const [images, setImages] = useState<PreviewImage[]>(loadPreviewImages);

  useEffect(() => {
    const handler = () => setImages(loadPreviewImages());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const topRow = images.slice(0, 3);
  const bottomRow = images.slice(3, 6);

  const renderCard = (img: PreviewImage, size: 'large' | 'small') => {
    const aspectClass = size === 'large'
      ? 'aspect-[3/4] md:aspect-[3/4]'
      : 'aspect-[4/5] md:aspect-[3/4]';

    return (
      <div
        key={img.id}
        className="group relative overflow-hidden rounded-xl md:rounded-2xl cursor-pointer"
        onClick={() => setSelectedImage({ src: img.src, alt: img.alt })}
      >
        <div className={`relative overflow-hidden ${aspectClass}`}>
          <EditableImage
            imageKey={`portfolio-preview-${img.id}`}
            src={img.src}
            alt={img.alt}
            containerClassName="w-full h-full"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          >
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-dark">
                <i className="ri-add-line text-lg" />
              </span>
            </div>
            <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-dark">
                {img.category}
              </span>
            </div>
          </EditableImage>
        </div>
      </div>
    );
  };

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-widest uppercase text-warm-600 mb-3 block">
              Portfólio
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-dark font-normal leading-tight">
              Registros <span className="italic">selecionados</span>
            </h2>
            <p className="mt-3 text-warm-700 text-sm md:text-base font-light">
              Fotografia afetiva e espontânea há mais de 10 anos.
            </p>
          </div>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-dark/20 text-dark text-sm font-medium hover:bg-dark hover:text-white transition-all duration-300 whitespace-nowrap self-start md:self-auto"
          >
            Ver todos os trabalhos
            <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>

        <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {topRow.map((img) => renderCard(img, 'large'))}
          </div>
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {bottomRow.map((img) => renderCard(img, 'small'))}
          </div>
        </div>
      </div>

      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}